import { PropsWithChildren } from "react";

import { css } from "@compiled/react";

import { wacToken } from "@/libraries/design/foundations";
import {
    useCloudStatus,
    useCloudStatusRedirection,
    useConfirmationPageType
} from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";

import ErrorView from "./error/error-view";
import { JiraAnimatedProvisioningScreen } from "./jira-animated-provisioning-screen/jira-animated-provisioning-screen";
import JiraTemplateChoiceViewManager from "./jira-template-choice/jira-choice-view-manager";
import RobotsView from "./robots/robots-view";
import TimeoutView from "./timeout/timeout-view";
import TipsView from "./tips/tips-view";
import JiraUserSegmentationView from "./user-seg-bxp-ui/user-segmentation";
import UserSegmentationView from "./user-segmentation/user-segmentation-view";

const ContainerStyle = css({
    display: "flex",
    flexFlow: "column",
    alignItems: "center",
    alignSelf: "center"
});
const InnerStyle = css({
    alignItems: "stretch",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    textAlign: "center",
    padding: "3rem",
    fontWeight: "normal",
    fontFamily: wacToken("font.charlie.text"),
    marginBlockStart: "30px"
});

export const LegacyConfirmationPageLayout = ({
    children
}: PropsWithChildren) => {
    return (
        <div css={ContainerStyle}>
            <div css={InnerStyle}>{children}</div>
        </div>
    );
};

/* istanbul ignore next */
const ViewSwitch = () => {
    let confirmationPageView = <RobotsView />;
    const { createCloudStatus, startPerceivedProvisioningTimer } =
        useCloudStatus();
    const { deferRedirectView } = useCloudStatusRedirection();
    const confirmationPageType = useConfirmationPageType(
        createCloudStatus,
        deferRedirectView
    );

    switch (confirmationPageType) {
        case "user-segmentation":
            /*
                Products that can view the purple user segmentation.
                jira-servicedesk.ondemand => JSM
                The purple user seg view will be shown to users who have not viewed it with the past 365 days. 
            */
            confirmationPageView = <UserSegmentationView />;
            break;
        case "jira-user-segmentation":
            // view for JSW/Jira N2N users only
            confirmationPageView = <JiraUserSegmentationView />;
            break;
        case "jira-template-selection":
            confirmationPageView = <JiraTemplateChoiceViewManager />;
            break;
        case "tips":
            confirmationPageView = <TipsView />;
            break;
        case "timeout":
            confirmationPageView = <TimeoutView />;
            break;
        case "error":
            confirmationPageView = <ErrorView />;
            break;
        case "jira-animated-provisioning-screen":
            startPerceivedProvisioningTimer();
            confirmationPageView = <JiraAnimatedProvisioningScreen />;
            break;
        case "redirect":
            startPerceivedProvisioningTimer();
            confirmationPageView = <div id="redirectToProductContainer" />;
            break;
        default:
            startPerceivedProvisioningTimer();
            confirmationPageView = <RobotsView />;
    }

    // do not wrap legacy layout around the jira template selection
    if (
        confirmationPageType === "jira-template-selection" ||
        confirmationPageType === "jira-user-segmentation"
    ) {
        return confirmationPageView;
    }

    // TODO: Confirm if each child component needs this confirmation page layout.
    return (
        <LegacyConfirmationPageLayout>
            {confirmationPageView}
        </LegacyConfirmationPageLayout>
    );
};

export default ViewSwitch;
